import type { LoginKind } from "@/features/auth/api/login";
import type { ReactNode } from "react";
import { signInFormText } from "./signInText";
import { ShieldCheck, AlertCircle, ArrowLeft } from "lucide-react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import BrandLogo from "@/components/common/BrandLogo";

type LoginPanelProps = {
  activeTab: LoginKind;
  onTabChange: (tab: LoginKind) => void;
  hideHeaders?: boolean;
  children: ReactNode;
};

export default function LoginPanel({ activeTab, onTabChange, hideHeaders = false, children }: LoginPanelProps) {
  const formText = signInFormText[activeTab];
  const searchParams = useSearchParams();
  const isSessionRevoked = searchParams.get("reason") === "session_taken_over";

  return (
    <section className="relative flex items-center justify-center p-5 sm:p-10 min-h-full">
      <div className="w-full max-w-md space-y-6 sm:space-y-7">
        {/* Mobile Header: Brand & Back link */}
        <div className="lg:hidden flex items-center justify-between pb-3 border-b border-[var(--border)]">
          <Link href="/">
            <BrandLogo size="sm" />
          </Link>
          <Link
            href="/"
            className="inline-flex items-center text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text-strong)] transition-colors"
          >
            <ArrowLeft className="mr-1 h-3.5 w-3.5" />
            Back
          </Link>
        </div>
        {isSessionRevoked && !hideHeaders && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-amber-600 dark:text-amber-400 text-sm">
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Logged Out</p>
              <p className="text-xs opacity-90 mt-0.5">
                Your session was logged out because your Smartflo account was logged in from another device.
              </p>
            </div>
          </div>
        )}

        {!hideHeaders && (
          <div>
            <h2 className="text-2xl font-semibold tracking-normal text-[var(--text-strong)]">
              {activeTab === "admin" ? "Super Admin Sign In" : "Sign in to workspace"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[var(--text-muted)]">
              {activeTab === "admin"
                ? "Restricted access. Verify your super admin credentials and OTP."
                : "Verify your credentials and enter the OTP sent to your email."}
            </p>
          </div>
        )}

        <div key={activeTab} className="space-y-4">
          {!hideHeaders && (
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-strong)]">{formText.title}</h3>
              <p className="mt-1 text-sm leading-6 text-[var(--text-muted)]">{formText.description}</p>
            </div>
          )}

          {children}
        </div>
      </div>

      {/* Discreet Super Admin corner button */}
      <button
        type="button"
        onClick={() => onTabChange(activeTab === "admin" ? "user" : "admin")}
        title={activeTab === "admin" ? "Switch to User Login" : "Super Admin Login"}
        className={`absolute bottom-4 right-4 flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-semibold transition-all border ${
          activeTab === "admin"
            ? "bg-[var(--accent-soft)] border-[var(--accent)] text-[var(--accent-strong)]"
            : "bg-transparent border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent-strong)]"
        }`}
      >
        <ShieldCheck className="h-3.5 w-3.5" />
        {activeTab === "admin" ? "User Login" : "Super Admin"}
      </button>
    </section>
  );
}
